<script setup lang="ts">
import { IEventTicketTier } from '@/enitites/event/model/types'
import BKInput from '@/components/AppShared/BKInput.vue'
import BKCurrencyInput from '@/components/AppShared/BKCurrencyInput.vue'
import { computed, ref, watch } from 'vue'
import useValidationRules from '@/shared/composables/validation'
import { useAddTier, useDeleteTier, useUpdateTier } from '@/modules/Payment/api/queries'
import { ITierPayload } from '@/modules/Payment/types'
import { BKConfirmationDialog, ModalWrapper } from '@/shared/ui/modals'
import Bugsnag from '@bugsnag/js'

const props = defineProps<{
  eventId: number
  isOpen: boolean
  ticketTier: IEventTicketTier | null
  totalBattles: number
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update:ticket-tier'): void
}>()

const tierTitle = ref(props.ticketTier ? props.ticketTier.tierTitle : '')
const price = ref(props.ticketTier ? props.ticketTier.price : 0)
const description = ref(props.ticketTier ? props.ticketTier.description : '')
const limit = ref(props.ticketTier?.limit)
const isNew = computed(() => !props.ticketTier?.id)
const isShowDeleteConfirmation = ref(false)

const isFormValid = ref(true)

const rules = useValidationRules()

const { mutate: addTier, isPending: isAddingTier } = useAddTier()
const { mutate: updateTier, isPending: isUpdatingTier } = useUpdateTier()
const { mutate: deleteTier, isPending: isDeletingTier } = useDeleteTier()

const isProcessing = computed(() => isAddingTier.value || isUpdatingTier.value || isDeletingTier.value)

function resetForm() {
  tierTitle.value = ''
  price.value = 0
  description.value = ''
  limit.value = undefined
}

function handleLimitUpdate(value: string | number) {
  if (typeof value === 'string') {
    if (value.trim() === '') {
      limit.value = 1
      return
    }

    const numericValue = parseInt(value, 10)

    if (Number.isNaN(numericValue)) {
      limit.value = 1
      return
    }

    limit.value = Math.max(1, numericValue)
  } else {
    limit.value = Math.max(1, value || 1)
  }
}

function handleSave() {
  if (isProcessing.value) return

  const payload: ITierPayload = {
    tierTitle: tierTitle.value,
    price: price.value,
    description: description.value,
    limit: limit.value,
  }

  const callbacks = {
    onSuccess: () => {
      emit('update:ticket-tier')
      resetForm()
    },
    onError: (err: Error) => {
      Bugsnag.notify(err, (event) => {
        event.addMetadata('TicketTierModal', {
          eventId: props.eventId,
          tierTitle: tierTitle.value,
          price: price.value,
          description: description.value,
          limit: limit.value,
        })
      })
    },
  }

  if (isNew.value) {
    addTier(
      {
        eventId: props.eventId,
        payload,
      },
      callbacks
    )
  } else if (props.ticketTier?.id) {
    updateTier(
      {
        tierId: props.ticketTier.id,
        payload,
      },
      callbacks
    )
  }
}

function handleDelete() {
  if (isProcessing.value || !props.ticketTier?.id) return
  isShowDeleteConfirmation.value = false

  deleteTier(
    {
      tierId: props.ticketTier.id,
      eventId: props.eventId,
    },
    {
      onSuccess: () => {
        emit('update:ticket-tier')
        resetForm()
      },
      onError: (err: Error) => {
        Bugsnag.notify(err, (event) => {
          event.addMetadata('TicketTierModal_Delete', {
            eventId: props.eventId,
            tierId: props.ticketTier?.id,
          })
        })
      },
    }
  )
}

function handleClose() {
  emit('close')
  resetForm()
}

watch(
  () => props.ticketTier,
  (newTier) => {
    if (newTier) {
      tierTitle.value = newTier.tierTitle
      price.value = newTier.price
      description.value = newTier.description
      limit.value = newTier.limit
    } else {
      resetForm()
    }
  },
  { immediate: true }
)
</script>

<template>
  <ModalWrapper
    :is-show="isOpen"
    modal-width="730px"
    @update:isShow="handleClose"
  >
    <v-card class="dialog-content">
      <v-card-title>
        <h2 class="dialog-header">{{ isNew ? 'Add ticket' : 'Edit ticket' }}</h2>
      </v-card-title>
      <v-card-text>
        <v-form
          v-model="isFormValid"
          class="form"
        >
          <BKInput
            v-model="tierTitle"
            label="Tier title"
            class="tier-title-input"
            :rules="[rules.required]"
          />
          <BKCurrencyInput
            v-model="price"
            label="Cost"
            class="price-input"
            :rules="[rules.required, rules.nonNegativeNumber]"
          />
          <BKInput
            v-model="description"
            label="Description"
            class="description-input"
          />
          <div class="limit-input-container">
            <p class="battles-included-title">Ticket Limits</p>
            <BKInput
              :value="limit"
              label="Limit"
              type="number"
              :min="1"
              class="limit-input"
              :rules="[rules.required, rules.nonNegativeNumber]"
              @input="handleLimitUpdate"
            />
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="modal-actions">
        <BKButton
          :disabled="!isFormValid || isProcessing"
          :loading="isProcessing"
          white-text
          @click="handleSave"
        >
          {{ isNew ? 'Create ticket' : 'Save changes' }}
        </BKButton>
        <BKButton
          v-if="!isNew"
          :disabled="isProcessing"
          text
          color="red"
          @click="isShowDeleteConfirmation = true"
        >
          Delete ticket
        </BKButton>
      </v-card-actions>
    </v-card>
    <BKConfirmationDialog
      :is-show="isShowDeleteConfirmation"
      title="Delete ticket tier"
      message="Are you sure you want to delete this ticket tier? This action cannot be undone."
      confirm-text="Delete"
      @confirm="handleDelete"
      @close="isShowDeleteConfirmation = false"
    />
  </ModalWrapper>
</template>

<style scoped>
.dialog-content {
  position: relative;
  padding: 24px 16px 32px;
  border-radius: 6px;

  @media screen and (max-width: 600px) {
    padding-top: 50px !important;
  }

  .dialog-header {
    font-size: 32px;
    line-height: 36px;
    font-weight: 700;
    color: var(--color-dark-800);
  }

  .form {
    display: grid;
    grid-template-areas:
      'tier-title price'
      'description description'
      'limit limit'
      'battles-included battles-included';
    grid-template-columns: 1fr 150px;
    gap: 8px;

    @media screen and (max-width: 600px) {
      grid-template-areas:
        'tier-title'
        'price'
        'description'
        'limit'
        'battles-included';
      grid-template-columns: 1fr;
    }
  }

  .tier-title-input {
    grid-area: tier-title;
  }

  .price-input {
    grid-area: price;
  }

  .description-input {
    grid-area: description;
    margin-bottom: 12px;
  }

  .battles-included-title {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 8px;
    color: var(--color-dark-800);
  }

  .limit-input-container {
    grid-area: limit;

    .limit-input {
      margin-top: 4px;
      width: 190px;
    }
  }

  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
